import { DialogComponent } from '@theme/dialog';

/**
 * A custom element that displays a full-screen image lightbox with slideshow navigation.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 * @property {HTMLElement} content – The content container element.
 * @property {HTMLButtonElement} prevButton – The previous button element.
 * @property {HTMLButtonElement} nextButton – The next button element.
 * @property {HTMLElement} counter – The counter element.
 *
 * @extends DialogComponent
 */
class ProductPopupLightbox extends DialogComponent {
  requiredRefs = ['dialog', 'content'];

  /** @type {string[]} */
  #images = [];

  /** @type {number} */
  #currentIndex = 0;

  /** @type {((event: KeyboardEvent) => void) | null} */
  #boundHandleKeydown = null;

  connectedCallback() {
    super.connectedCallback();
    this.#boundHandleKeydown = this.#handleKeydown.bind(this);

    // Listen for dialog close events to clean up keyboard listener
    this.addEventListener('dialog:close', this.#onDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('dialog:close', this.#onDialogClose);
    document.removeEventListener('keydown', this.#boundHandleKeydown);
  }

  /**
   * Cleans up when dialog closes.
   */
  #onDialogClose = () => {
    document.removeEventListener('keydown', this.#boundHandleKeydown);
  };

  /**
   * Opens the lightbox at a specific image index.
   *
   * @param {number} index - The index of the image to display.
   * @param {string[]} images - Array of image URLs.
   */
  openAtIndex(index, images) {
    this.#images = images;
    this.#currentIndex = index;

    this.#renderImage();
    this.#updateNavigation();
    this.showDialog();

    // Add keyboard listener
    document.addEventListener('keydown', this.#boundHandleKeydown);
  }

  /**
   * Handles keyboard navigation.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeydown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previousImage();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.nextImage();
    }
  }

  /**
   * Shows the previous image.
   */
  previousImage = () => {
    if (this.#currentIndex > 0) {
      this.#currentIndex--;
      this.#renderImage();
      this.#updateNavigation();
    }
  };

  /**
   * Shows the next image.
   */
  nextImage = () => {
    if (this.#currentIndex < this.#images.length - 1) {
      this.#currentIndex++;
      this.#renderImage();
      this.#updateNavigation();
    }
  };

  /**
   * Updates the navigation buttons and counter.
   */
  #updateNavigation() {
    const { prevButton, nextButton, counter } = this.refs;

    // Update button states
    if (prevButton instanceof HTMLButtonElement) {
      prevButton.disabled = this.#currentIndex === 0;
    }
    if (nextButton instanceof HTMLButtonElement) {
      nextButton.disabled = this.#currentIndex === this.#images.length - 1;
    }

    // Update counter
    if (counter) {
      counter.textContent = `${this.#currentIndex + 1} / ${this.#images.length}`;
    }

    // Hide navigation if only one image
    const showNav = this.#images.length > 1;
    if (prevButton) prevButton.style.display = showNav ? '' : 'none';
    if (nextButton) nextButton.style.display = showNav ? '' : 'none';
    if (counter) counter.style.display = showNav ? '' : 'none';
  }

  /**
   * Renders the current image in the lightbox.
   */
  #renderImage() {
    const { content } = this.refs;
    const imageUrl = this.#images[this.#currentIndex];

    if (!imageUrl || !content) return;

    content.innerHTML = `
      <img
        src="${imageUrl}"
        alt=""
        class="product-popup-lightbox__image"
      />
    `;
  }
}

if (!customElements.get('product-popup-lightbox')) {
  customElements.define('product-popup-lightbox', ProductPopupLightbox);
}

export { ProductPopupLightbox };
